import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useChatStore } from '../store/chatStore';
import { roomsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';

export default function ChatRoomsPage() {
  const navigate = useNavigate();
  const { user, anonymousUser, logout, isAuthenticated, isAnonymous, initializeAnonymous } = useAuthStore();
  const { rooms, setRooms } = useChatStore();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Initialize anonymous user if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      initializeAnonymous();
    }
  }, [isAuthenticated, initializeAnonymous]);

  useEffect(() => {
    const loadRooms = async () => {
      try {
        const roomsList = await roomsApi.list();
        setRooms(roomsList);
      } catch (error) {
        console.error('Failed to load rooms:', error);
      }
    };
    loadRooms();
  }, [setRooms]);

  // Filter rooms based on selected category
  const filteredRooms = selectedCategory === null || selectedCategory === 'General'
    ? rooms
    : rooms.filter(room => room.category === selectedCategory);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center relative">
          {/* Left spacer for balance */}
          <div className="w-1/3"></div>
          
          {/* Center: Logo */}
          <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-3">
            <img 
              src="/assets/TheWorldBridger.png" 
              alt="TheWorldBridger Logo" 
              className="h-12 w-auto"
            />
            <h1 className="text-2xl font-bold">TheWorldBridger</h1>
          </div>
          
          {/* Right: User actions */}
          <div className="flex items-center gap-4 w-1/3 justify-end">
            {isAuthenticated ? (
              <>
                <span className="text-sm text-muted-foreground">
                  {isAnonymous ? anonymousUser?.username : user?.username}
                </span>
                {!isAnonymous && (
                  <Button variant="outline" onClick={logout}>
                    Logout
                  </Button>
                )}
                {isAnonymous && (
                  <>
                    <Link to="/login">
                      <Button variant="outline" size="sm">Sign In</Button>
                    </Link>
                    <Link to="/register">
                      <Button size="sm">Sign Up</Button>
                    </Link>
                  </>
                )}
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="outline">Sign In</Button>
                </Link>
                <Link to="/register">
                  <Button>Sign Up</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="container mx-auto px-4 py-2 max-w-7xl">
        {/* World Bridger Logo with Text */}
        <div className="mb-2 flex justify-center pt-2">
          <img 
            src="/assets/World Bridger With Text.png" 
            alt="TheWorldBridger Logo" 
            className="h-[117px] md:h-[175px] lg:h-[464px] w-auto"
          />
        </div>

        {/* Category Selection - Four large buttons */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-4 text-center">Choose a Category</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* General Category */}
            <Card
              className={`cursor-pointer hover:shadow-xl transition-all hover:scale-105 border-2 aspect-square flex flex-col w-full ${
                (selectedCategory === null || selectedCategory === 'General')
                  ? 'border-primary bg-primary/5 shadow-lg'
                  : 'hover:border-primary'
              }`}
              onClick={() => setSelectedCategory(selectedCategory === 'General' ? null : 'General')}
            >
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <img 
                  src="/assets/General.png" 
                  alt="General" 
                  className="h-64 w-auto mb-6"
                />
                <CardTitle className="text-2xl mb-2">General</CardTitle>
                <p className="text-center text-muted-foreground">
                  General discussions and conversations on any topic
                </p>
              </div>
            </Card>

            {/* Career Category */}
            <Card
              className={`cursor-pointer hover:shadow-xl transition-all hover:scale-105 border-2 aspect-square flex flex-col w-full ${
                selectedCategory === 'Career'
                  ? 'border-primary bg-primary/5 shadow-lg'
                  : 'hover:border-primary'
              }`}
              onClick={() => setSelectedCategory(selectedCategory === 'Career' ? null : 'Career')}
            >
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <img 
                  src="/assets/Career.png" 
                  alt="Career" 
                  className="h-64 w-auto mb-6"
                />
                <CardTitle className="text-2xl mb-2">Career</CardTitle>
                <p className="text-center text-muted-foreground">
                  Connect with professionals and discuss career opportunities
                </p>
              </div>
            </Card>

            {/* Entertainment Category */}
            <Card
              className={`cursor-pointer hover:shadow-xl transition-all hover:scale-105 border-2 aspect-square flex flex-col w-full ${
                selectedCategory === 'Entertainment'
                  ? 'border-primary bg-primary/5 shadow-lg'
                  : 'hover:border-primary'
              }`}
              onClick={() => setSelectedCategory(selectedCategory === 'Entertainment' ? null : 'Entertainment')}
            >
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <img 
                  src="/assets/Entertainment.png" 
                  alt="Entertainment" 
                  className="h-64 w-auto mb-6"
                />
                <CardTitle className="text-2xl mb-2">Entertainment</CardTitle>
                <p className="text-center text-muted-foreground">
                  Share and discuss movies, music, games, and more
                </p>
              </div>
            </Card>

            {/* Health Category */}
            <Card
              className={`cursor-pointer hover:shadow-xl transition-all hover:scale-105 border-2 aspect-square flex flex-col w-full bg-transparent ${
                selectedCategory === 'Health'
                  ? 'border-primary shadow-lg'
                  : 'hover:border-primary'
              }`}
              onClick={() => setSelectedCategory(selectedCategory === 'Health' ? null : 'Health')}
            >
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <img 
                  src="/assets/Health.png" 
                  alt="Health" 
                  className="h-64 w-auto mb-6"
                />
                <CardTitle className="text-2xl mb-2">Health</CardTitle>
                <p className="text-center text-muted-foreground">
                  Wellness, fitness, and health-related discussions
                </p>
              </div>
            </Card>
          </div>
        </div>

        {/* All Rooms Section */}
        <div className="mt-12">
          <h2 className="text-3xl font-bold mb-6">
            {selectedCategory ? `${selectedCategory} Chat Rooms` : 'All Chat Rooms'}
          </h2>
          {filteredRooms.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">
                {selectedCategory 
                  ? `No chat rooms available in the ${selectedCategory} category.`
                  : 'No chat rooms available. Create one to get started!'
                }
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredRooms.map((room) => (
                <Card
                  key={room.id}
                  className="cursor-pointer hover:shadow-lg transition-shadow hover:border-primary"
                  onClick={() => navigate(`/room/${room.id}`)}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span>💬</span>
                      {room.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      {room.description || 'No description'}
                    </p>
                    {room.category && (
                      <span className="inline-block mt-2 text-xs px-2 py-1 bg-primary/10 text-primary rounded">
                        {room.category}
                      </span>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

